import { useState } from 'react';
import { Button } from '../../Components/UI/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../Components/UI/dropdown-menu';
import { ChevronDown, Plus, FileText, ShoppingBag, ArrowLeft, Search, X } from 'lucide-react';
import useIsMobile from '../../Hooks/useIsMobile';
import { currentUserHasPermission } from '../../Utils/Utilities';
import MobileFullScreenDropdown from '../../Components/Common/MobileDropdown';

const ContractDropdown = ({
    className = "",
    onContractTypeSelect,
    connectionType = null,
    externalOpen = false,
    onExternalOpenChange = null,
    title = "Add Contract",
    minimalIcon = false,
    dropdownAlign = "end"
}) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const isMobile = useIsMobile();
    const perm = currentUserHasPermission('CtrcActions');

    const canAddCtrc = perm === 1 || perm === 2 || perm === 3;

    // Permission check - if no access, don't render
    if (!canAddCtrc) {
        return null;
    }

    // Use external control if provided, otherwise use internal state
    const isOpen = onExternalOpenChange ? externalOpen : internalOpen;
    const setIsOpen = onExternalOpenChange ? onExternalOpenChange : setInternalOpen;

    // All contract type options
    const allContractTypes = [
        {
            id: 'sold',
            label: 'Customer Contract',
            icon: FileText,
            colorClass: 'text-sahi-green'
        },
        {
            id: 'purchase',
            label: 'Supplier Contract',
            icon: FileText,
            colorClass: 'text-sahi-orange'
        }
    ];

    // Filter contract types based on permissions
    const getFilteredContractTypes = () => {
        switch (perm) {
            case 1: // All - show both customer and supplier
            case 4: // Assigned only - show both customer and supplier
                return allContractTypes;

            case 2: // Modify (treated as customer + supplier add too)
                return allContractTypes;

            case 3: // Create supplier only
                return allContractTypes.filter(type => type.id === 'purchase');

            default: // 0 or anything else = no access
                return [];
        }
    };


    // Contract type options based on permissions
    const contractTypes = getFilteredContractTypes();

    // Handle contract type selection
    const handleContractTypeSelect = (contractType) => {
        if (onContractTypeSelect) {
            onContractTypeSelect(contractType);
        }
        setIsOpen(false);
    };

    // If connection type is specified, directly open contract entry
    if (connectionType === 'C') {
        return minimalIcon ? (
            <Plus
                className={`h-6 w-6 text-gray-600 hover:text-gray-800 cursor-pointer transition-colors ${className}`}
                onClick={() => handleContractTypeSelect('sold')}
            />
        ) : (
            <Button
                className={`${className} flex items-center gap-2`}
                onClick={() => handleContractTypeSelect('sold')}
            >
                <Plus className="h-4 w-4" />
                {title}
            </Button>
        );
    }

    if (connectionType === 'S') {
        // Supplier connection - directly create purchase contract
        return minimalIcon ? (
            <Plus
                className={`h-6 w-6 text-gray-600 hover:text-gray-800 cursor-pointer transition-colors ${className}`}
                onClick={() => handleContractTypeSelect('purchase')}
            />
        ) : (
            <Button
                className={`${className} flex items-center gap-2`}
                onClick={() => handleContractTypeSelect('purchase')}
            >
                <Plus className="h-4 w-4" />
                {title}
            </Button>
        );
    }

    // If only one contract type available, directly handle selection
    if (contractTypes.length === 1) {
        const singleContractType = contractTypes[0];
        return minimalIcon ? (
            <Plus
                className={`h-6 w-6 text-gray-600 hover:text-gray-800 cursor-pointer transition-colors ${className}`}
                onClick={() => handleContractTypeSelect(singleContractType.id)}
                data-contract-dropdown-trigger
            />
        ) : (
            <Button
                className={`${className} flex items-center gap-2`}
                onClick={() => handleContractTypeSelect(singleContractType.id)}
                data-contract-dropdown-trigger
            >
                <Plus className="h-4 w-4" />
                {title}
            </Button>
        );
    }

    // Mobile view - use MobileFullScreenDropdown
    if (isMobile) {
        return (
            <MobileFullScreenDropdown
                options={contractTypes}
                value={null}
                onSelect={(selectedValue, selectedOption) => {
                    handleContractTypeSelect(selectedValue);
                }}
                placeholder="Select Contract Type"
                displayKey="label"
                valueKey="id"
                className={className}
                label="Contract Type"
                emptyMessage="No contract types available"
                searchThreshold={9999}
                searchPlaceholder="Search contract types..."
                isIcon={minimalIcon}
                showButtonStyle={!minimalIcon}
                buttonTitle={title}
                showClearButton={false}
                showAllOption={false}
                isSorting={false}
                mobileDropdownText="Select Contract Type"
                externalOpen={externalOpen}
                onExternalOpenChange={onExternalOpenChange}
                renderOption={(option, isSelected) => (
                    <div className="flex items-center gap-3 w-full">
                        <option.icon className={`h-5 w-5 ${option.colorClass}`} />
                        <span className="font-medium text-gray-900">
                            {option.label}
                        </span>
                    </div>
                )}
            />
        );
    }

    // Desktop view - use existing dropdown implementation
    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                {minimalIcon ? (
                    <Plus
                        className={`h-6 w-6 text-gray-600 hover:text-gray-800 cursor-pointer transition-colors ${className}`}
                        data-contract-dropdown-trigger
                    />
                ) : (
                    <Button
                        className={`${className} flex items-center gap-2`}
                        data-contract-dropdown-trigger
                    >
                        <Plus className="h-4 w-4" />
                        {title}
                        {contractTypes.length > 1 && <ChevronDown className="h-4 w-4" />}
                    </Button>
                )}
            </DropdownMenuTrigger>

            <DropdownMenuContent
                className="w-50 min-w-0 bg-white border border-gray-200 shadow-lg rounded-md"
                align={dropdownAlign}
            >
                {contractTypes.map((contractType) => (
                    <DropdownMenuItem
                        key={contractType.id}
                        onClick={() => handleContractTypeSelect(contractType.id)}
                        className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 focus:bg-gray-50 bg-white border-0"
                    >
                        <contractType.icon className={`h-4 w-4 ${contractType.colorClass}`} />
                        <span className="font-medium ">
                            {contractType.label}
                        </span>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default ContractDropdown;